import { Component, DestroyRef, Inject, Injector } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AttributeShortInfo } from '@cloud-twin-v2/ct-map/models/attribute-short-info';
import { CTMapDashboardBridgeService } from '@cloud-twin-v2/ct-map/services/ct-map-dashboard-bridge.service';
import { CTResultsFormatService } from '@cloud-twin-v2/ct-map/services/ct-results-format.service';
import { MasterOperatorFields } from '@cloud-twin/master-operator/models/master-operator-fields.enum';
import { WidgetSettingsToken } from '@common-modules/dynamic-layout/dynamic-layout.tokens';
import { GetValueSelector } from '@common-modules/dynamic-layout/state/generic/generic.selectors';
import { BaseDynamicWidgetComponent } from '@common-modules/redux/components/base-dynamic-widget.component';
import { StateWidgetSettings } from '@common-modules/redux/models/state-widget-settings';
import { ReduxStateService } from '@common-modules/redux/redux-state.service';
import { GridSetting } from '@common-modules/shared/constants/grid.constants';

@Component({
  selector: 'ct-type-attribute-grid-widget',
  templateUrl: './ct-type-attribute-grid-widget.component.html',
  styleUrl: './ct-type-attribute-grid-widget.component.scss',
  standalone: false,
  providers: [ReduxStateService],
})
export class CtTypeAttributeGridWidgetComponent extends BaseDynamicWidgetComponent {
  type: string;
  attributes: AttributeShortInfo[];
  gridSetting: GridSetting;
  data: { [property: string]: string }[];
  additionalColumnsByScenario: { [scenarioId: string]: { [property: string]: string }[] }[] = [];
  connectionColumns = [
    {
      field: 'modelName',
      visible: true,
      title: 'Model',
      width: 150,
      type: 'text',
    },
    {
      field: 'modelVersionName',
      visible: true,
      title: 'Model Version',
      width: 150,
      type: 'text',
    },
    {
      field: 'scenarioName',
      visible: true,
      title: 'Scenario',
      width: 150,
      type: 'text',
    },
  ];

  private readonly _columnTypeAttribute = 'attribute';
  private readonly _columnTypeInternal = 'internal';
  private readonly _columnTypeExportOnly = 'export-only';

  get componentName(): string {
    return 'CtTypeAttributeGridWidgetComponent';
  }

  constructor(
    injector: Injector,
    @Inject(WidgetSettingsToken) widgetSettings: StateWidgetSettings,
    private readonly _destroyRef: DestroyRef,
    private readonly _resultsFormatService: CTResultsFormatService,
    private readonly _attributeResultsBridge: CTMapDashboardBridgeService
  ) {
    super(injector, widgetSettings);
    this.type = widgetSettings.params.type;
    this.attributes = widgetSettings.params.attributes;
    this.data = widgetSettings.params.data;
  }

  onWidgetInit(): void {
    this.gridSetting = this.buildDynamicGridSetting();
    this.listenGridData();
  }

  buildDynamicGridSetting(): any {
    const gridSettings: any = {
      count: true,
      filterable: 'menu',
      navigatable: false,
      pageSize: 20,
      ignoreCase: true,
      pageable: {
        type: 'input',
        pageSizes: [10, 20, 50],
      },
      reorderable: true,
      resizable: true,
      selectable: {
        enabled: true,
        mode: 'multiple',
        drag: true,
        cell: true,
        multipleRanges: true,
      },
      showSelectAllColumn: false,
      showZoneColumns: false,
      sortable: true,
      useUoM: false,
      gridColumnSettings: [
        {
          field: 'scenarioId',
          visible: false,
          title: 'scenarioId',
          width: 150,
          type: 'text',
        },
        {
          field: 'networkElementId',
          visible: false,
          title: 'networkElementId',
          width: 150,
          type: 'text',
        },
        {
          field: 'networkElementName',
          visible: true,
          title: 'Network Element Name',
          width: 150,
          type: 'text',
        },
      ],
      selectByFieldName: 'networkElementId',
      buttons: {
        show: ['clear_grid_filters', 'export_excel'],
      },
      sort: [
        {
          field: 'networkElementName',
          dir: 'desc',
        },
      ],
      excelFileName: this.type,
      selectionPersistency: false,
      hideHeader: false,
      utcDates: false,
    };

    if (this.attributes && this.attributes.length > 0) {
      this.attributes.forEach((attribute: AttributeShortInfo) => {
        const fieldName = attribute.field || '';
        const titleName = attribute.alias || '';

        let columns = [];
        switch (attribute.attributeTypeKey) {
          case 'Double':
            columns = this.getDoubleColumns(attribute);
            break;

          case 'Boolean':
            columns = this.getCommonColumn(fieldName, titleName, 'boolean');
            break;

          default:
            columns = this.getCommonColumn(fieldName, titleName);
            break;
        }

        gridSettings.gridColumnSettings = [...gridSettings.gridColumnSettings, ...columns];
      });
    }

    gridSettings.gridColumnSettings = [
      ...gridSettings.gridColumnSettings,
      ...this.connectionColumns,
    ];

    return gridSettings;
  }

  getDoubleColumns(attribute: AttributeShortInfo): any[] {
    const fieldName = attribute.field;
    const titleName = attribute.alias;
    const formatFieldName = `${fieldName}_format`;
    const unitFieldName = `${fieldName}_units`;

    var columns = this.getCommonColumn(fieldName, titleName, this._columnTypeAttribute);

    if (attribute?.format) {
      columns = [
        ...columns,
        ...this.getCommonColumn(formatFieldName, titleName, this._columnTypeInternal, false),
      ];
      this.insertColumnMetadata(attribute.scenarioId, formatFieldName, attribute.format);
    }

    if (attribute?.units) {
      columns = [
        ...columns,
        ...this.getCommonColumn(
          unitFieldName,
          `${titleName} [Units]`,
          this._columnTypeExportOnly,
          false,
          fieldName
        ),
      ];
      const unitValue = attribute.units == '-' ? '' : attribute.units;
      this.insertColumnMetadata(attribute.scenarioId, unitFieldName, unitValue);
    }

    return columns;
  }

  insertColumnMetadata(scenarioId: string, fieldName: string, value: string) {
    if (!this.additionalColumnsByScenario[scenarioId]) {
      this.additionalColumnsByScenario[scenarioId] = [];
    }

    const metadataColumn: { [additionalField: string]: string } = {};

    metadataColumn[fieldName] = value ?? '';

    this.additionalColumnsByScenario[scenarioId].push(metadataColumn);
  }

  getCommonColumn(
    fieldName: string,
    titleName: string,
    type: string = 'text',
    visible: boolean = true,
    referenceColumn = null
  ) {
    const column = {
      field: fieldName,
      visible: visible,
      title: titleName,
      width: 150,
      type: type,
    };

    if (referenceColumn) {
      column['referenceColumn'] = referenceColumn;
    }

    return [column];
  }

  private listenGridData(): void {
    this._state
      .select<any>(
        new GetValueSelector({
          fieldName: MasterOperatorFields.AttributeResults,
        })
      )
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((value) => {
        if (value) {
          this._attributeResultsBridge.setAttributeResults(value);
          const dataFilteredByType =
            value.find((f) => f.networkElementTypeId == this.type)?.values ?? [];

          const dataWithAdditionalColumnInfo =
            this._resultsFormatService.mergeDataWithAdditionalColumns(
              dataFilteredByType,
              this.additionalColumnsByScenario
            );

          const attributeColumns = this.gridSetting.gridColumnSettings
            .filter((x) => x.type === this._columnTypeAttribute)
            ?.map((x) => x.field);

          if (attributeColumns && attributeColumns.length) {
            dataWithAdditionalColumnInfo.forEach((row) => {
              attributeColumns.forEach((field) => {
                if (row[field] == 'NaN') {
                  row[field] = null;
                }
              });
            });
          }

          this.data = dataWithAdditionalColumnInfo;
        }
      });
  }
}
